import { Box, Flex, Radio, RadioGroup, Text } from '@chakra-ui/react'
import { ExamSectionData, ExamSectionField } from '@hb/shared'
import { CSSProperties, forwardRef, useCallback, useMemo } from 'react'
import TextAreaAutosize from 'react-textarea-autosize'
import { InputElement, InputProps } from '../../../../types/fields'
import { useFormWidth } from '../../FinalForm/context'
import InputContainer, {
  getInputContainerPaddingX,
  getInputContainerPaddingY,
} from '../Shared/InputContainer'
import { getInputSizeTokens, useInputSize } from '../Shared/sizes'
import { baseInputStyle } from '../Shared/styles'

export const ExamSectionInput: InputElement<ExamSectionField> = forwardRef<
  HTMLInputElement,
  InputProps<ExamSectionField>
>((props, _ref) => {
  const { input, field, disabled, readOnly } = props
  const { placeholder } = field
  const size = useInputSize(props.size ?? field?.size)
  const { mobileLayout } = useFormWidth()
  const value = useMemo<ExamSectionData>(
    () => (typeof input.value === 'object' && input.value ? input.value : {}),
    [input.value],
  )

  const handleValueChange = useCallback(
    (newVal: string) => {
      input.onChange({
        ...value,
        value: newVal as 'normal' | 'abnormal',
      })
      input.onBlur()
    },
    [input, value],
  )

  const handleNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      input.onChange({
        ...value,
        notes: e.target.value || undefined,
      })
    },
    [input, value],
  )

  return (
    <Flex
      flexFlow={mobileLayout ? 'column' : 'row'}
      w="100%"
      pt={getInputContainerPaddingY(size)}
      gap={mobileLayout ? 0 : 4}>
      <Text
        width={mobileLayout ? '100%' : '25%'}
        maxW={mobileLayout ? '100%' : '160px'}
        fontSize={size === 'xs' || size === 'sm' ? 'sm' : 'md'}
        fontWeight={600}
        color="gray.600">
        {placeholder}
      </Text>
      <RadioGroup
        value={value.value ?? ''}
        onChange={handleValueChange}
        w={mobileLayout ? '100%' : 'auto'}
        isDisabled={disabled || readOnly}>
        <Flex
          w={mobileLayout ? '100%' : 'auto'}
          flexFlow={mobileLayout ? 'row wrap' : 'row'}
          py={size === 'xs' || size === 'sm' || mobileLayout ? 1 : 2}
          gap={4}
          align="center">
          <Radio value="normal" colorScheme="green" size={getInputSizeTokens(size).checkboxSize}>
            <Text fontSize="12px" color="#333333" fontFamily="Hero-New">
              Normal
            </Text>
          </Radio>
          <Radio value="abnormal" colorScheme="red" size={getInputSizeTokens(size).checkboxSize}>
            <Text fontSize="12px" color="#333333" fontFamily="Hero-New">
              Abnormal
            </Text>
          </Radio>
        </Flex>
      </RadioGroup>
      <Box flex={mobileLayout ? 'unset' : 1} w={mobileLayout ? '100%' : 'auto'} minW={0}>
        <InputContainer
          {...props}
          flexProps={{ py: 0, px: 0 }}
          style={{
            width: '100%',
            maxHeight: '350px',
            overflowY: 'auto',
            background: 'white',
            alignItems: 'flex-start',
          }}>
          <TextAreaAutosize
            value={value.notes ?? ''}
            onChange={handleNotesChange}
            onFocus={input.onFocus}
            onBlur={input.onBlur}
            placeholder="Notes..."
            disabled={disabled}
            readOnly={readOnly}
            style={{
              ...(baseInputStyle(size, disabled) as Omit<CSSProperties, 'height'>),
              padding: `${getInputContainerPaddingY(size)} ${getInputContainerPaddingX(size)}`,
            }}
          />
        </InputContainer>
      </Box>
    </Flex>
  )
})
